import React, { useState, useEffect, useMemo } from 'react';
import { styles, Modal } from '../theme';
import { API_URL, fetchWithAuth } from '../api';

const PosCategoryAssignItemsModal = ({ token, category, onClose, onSaved }) => {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!category) return;
    setLoading(true);
    fetchWithAuth(`${API_URL}/pos/categories/${category.id}/assignable-items`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        setItems(list);
        setSelected(new Set(list.filter(i => i.assigned).map(i => i.id)));
      })
      .catch(err => console.error('Error fetching assignable items:', err))
      .finally(() => setLoading(false));
  }, [category, token]);

  const filteredItems = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(i =>
      (i.item_name || '').toLowerCase().includes(q) ||
      (i.item_code || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/pos/categories/${category.id}/assign-items`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ item_ids: Array.from(selected) })
      });
      if (res.ok) {
        onSaved();
        onClose();
      } else {
        const errData = await res.json();
        alert('Error: ' + (errData.error || JSON.stringify(errData.error)));
      }
    } catch (err) {
      console.error('Error assigning items:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={!!category} onClose={onClose} title={`Assign Items — ${category?.name || ''}`} maxWidth="500px">
      <div style={styles.formGroup}>
        <input
          type="text"
          placeholder="Search items..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.input}
        />
      </div>

      {loading ? (
        <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>Loading items...</div>
      ) : items.length === 0 ? (
        <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
          No POS-enabled items yet. Enable items for POS from the Items screen first.
        </div>
      ) : (
        <div style={{
          maxHeight: '320px',
          overflowY: 'auto',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          marginTop: '8px'
        }}>
          {filteredItems.map(item => (
            <label
              key={item.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 12px',
                borderBottom: '1px solid #f3f4f6',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              <input
                type="checkbox"
                checked={selected.has(item.id)}
                onChange={() => toggle(item.id)}
              />
              <span style={{ flex: 1 }}>{item.item_name}</span>
              <span style={{ color: '#9ca3af', fontSize: '12px' }}>{item.item_code}</span>
            </label>
          ))}
        </div>
      )}

      <div style={{ marginTop: '20px', display: 'flex', gap: '12px', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '13px', color: '#6b7280' }}>{selected.size} item(s) selected</span>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={onClose} style={{ ...styles.button, ...styles.buttonSecondary }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={styles.button}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PosCategoryAssignItemsModal;
